import React, {
  useState,
  ForwardRefRenderFunction,
  useImperativeHandle,
  forwardRef,
  useRef,
  useEffect,
} from "react";

import { useToaster } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { FaArrowRightLong } from "react-icons/fa6";
import { FiCheck } from "react-icons/fi";
import { ReactSVG } from "react-svg";

import { Color } from "../../../../../../components/Form/Button";
import { ActionButtonProps } from "../../../../../../components/Generic/ActionButtons";
import SendEmailModal, {
  SendEmailModalHandle,
} from "../../../../../../components/Generic/SendEmailModal";
import ShareShipmentModal, {
  ShareShipmentModalHandler,
} from "../../../../../../components/Generic/ShareShipmentModal";
import LoadingModal from "../../../../../../components/LoadingModal";
import { useActionQuote } from "../../../../../../hooks/useActionQuote";
import { Sales } from "../../../../../../models/sales";
import {
  ShareNotificationId,
  ShareOptions,
  UsedInEmailTemplates,
} from "../../../../../../util/enums";
import {
  Wrapper,
  StylishModal,
  Card,
  CardBody,
  CardTitle,
  CardText,
  WrapperCardBody,
  LoadingContainer,
  CardLoading,
} from "./styles";

interface SendToCustomerProps {
  shipmentPhones?: string[];
  shipmentEmails?: string[];
}

export interface SendToCustomerHandle {
  setOpen: (value: boolean) => void;
  setShipmentId: (value?: number) => void;
  setContainerId: (value?: string) => void;
}

const EMAIL_SMS_NOTIFICATION_ICON = require("../../../../../../assets/icons/outline-sms-notification.svg");
const SMS_TRACKING_ICON = require("../../../../../../assets/icons/sms-tracking.svg");

const SendCommunicationModal: ForwardRefRenderFunction<
  SendToCustomerHandle,
  SendToCustomerProps
> = (props, ref) => {
  const { t: translate } = useTranslation();
  const { shipmentPhones, shipmentEmails } = props;
  const { sendToCustomerLoading } = useActionQuote();
  const { toasts } = useToaster();

  const [open, setOpen] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [selectedOption, setSelectedOption] = useState<string>("");

  const [shipmentId, setShipmentId] = useState<number>();
  const [containerId, setContainerId] = useState<string>();

  const sendEmailModalRef = useRef<SendEmailModalHandle>(null);
  const shareLinkModalRef = useRef<ShareShipmentModalHandler>(null);

  useImperativeHandle(ref, () => ({
    setOpen,
    setShipmentId,
    setContainerId,
  }));

  const isSaveButtonDisabled = currentStep === 0 && !selectedOption.trim().length;

  const getTextSaveBtn = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "4px",
      }}
    >
      <FaArrowRightLong size="15" />
      <div>{translate("next")}</div>
    </div>
  );

  const actionButtonProps: ActionButtonProps = {
    dataCySave: "submitButton",
    dataCyCancel: "cancelButton",
    textSave: getTextSaveBtn(),
    textCancel: currentStep ? translate("back") : translate("cancel"),
    customColorSave: Color.SUCCESS,
    customColorCancel: currentStep ? Color.TEXT_INFO : Color.CANCEL,
    saveDisabled: isSaveButtonDisabled,
    onSave: () => onSaveAction(),
    onCancel: currentStep
      ? () => setCurrentStep(0)
      : () => {
          setOpen(false);
          setSelectedOption("");
        },
  };

  const onSaveAction = () => {
    if (selectedOption === ShareOptions.SHARE_EMAIL) {
      return sendEmailModalRef.current?.setOpenModal(translate("sendEmail"));
    }

    if (selectedOption === ShareOptions.SHARE_SMS_OR_EMAIL) {
      shareLinkModalRef.current?.setShipmentId(shipmentId);
      shareLinkModalRef.current?.handleSetEmailsAndPhones(
        shipmentEmails || [],
        shipmentPhones || []
      );

      return shareLinkModalRef.current?.setOpen(true);
    }

    return null;
  };

  type OptionShare = {
    icon?: JSX.Element;
    key: string;
    title?: string;
    text?: string;
    action?: () => void;
    isLoading?: boolean;
    disabled?: boolean;
  };

  const shareOptions: OptionShare[] = [
    {
      icon: <ReactSVG src={EMAIL_SMS_NOTIFICATION_ICON} />,
      key: ShareOptions.SHARE_SMS_OR_EMAIL,
      title: translate("sendTrackingViaSMSorEmail"),
      text: translate("sendTheShipmentTrackingViaSMSOrEmailToTheCustomerOrSomeoneElse"),
      action: () => setSelectedOption(ShareOptions.SHARE_SMS_OR_EMAIL),
    },
    {
      icon: <ReactSVG src={SMS_TRACKING_ICON} />,
      key: ShareOptions.SHARE_EMAIL,
      title: translate("sendEmail"),
      text: translate("writeAnEmailOrUseATemplate"),
      action: () => setSelectedOption(ShareOptions.SHARE_EMAIL),
    },
  ];

  const renderShareOptions = () => {
    return shareOptions.map((option, index: number) => (
      <Card
        key={`option-${index + 1}`}
        disabled={option?.disabled}
        active={selectedOption === option.key}
        onClick={option?.action}
      >
        {option?.icon}
        <WrapperCardBody>
          <CardBody>
            <CardTitle>{option?.title || ""}</CardTitle>
            <CardText>{option?.text || ""}</CardText>
          </CardBody>
          {selectedOption === option.key ? <FiCheck size="18" /> : null}
        </WrapperCardBody>
        {option?.isLoading && (
          <LoadingContainer>
            <CardLoading size={20} />
          </LoadingContainer>
        )}
      </Card>
    ));
  };

  const modalTitle = currentStep
    ? translate("wouldYouLikeToSendTheDetailsOfTheCallToYourCustomerBy")
    : translate("whatDoYouWantToSend");

  const onCloseModal = () => {
    setSelectedOption("");
    setCurrentStep(0);
    setOpen(false);
  };

  useEffect(() => {
    if (toasts.length) {
      const hasToastFromShareEmail = toasts.find(
        (toast) => toast.id.toLowerCase() === ShareNotificationId.SHARE_SHIPMENT_EMAIL
      );
      const hasToastFromShareSms = toasts.find(
        (toast) => toast.id.toLowerCase() === ShareNotificationId.SHARE_SHIPMENT_SMS
      );
      const hasToastFromSendEmail = toasts.find(
        (toast) => toast.id.toLowerCase() === ShareNotificationId.SEND_SHIPMENT_EMAIL
      );

      if (hasToastFromShareEmail || hasToastFromShareSms || hasToastFromSendEmail) {
        onCloseModal();
        shareLinkModalRef?.current?.setOpen(false);
        sendEmailModalRef?.current?.onCloseModal();
      }
    }
  }, [toasts]);

  return (
    <StylishModal
      open={open}
      handleClose={onCloseModal}
      showActionButtons
      title={modalTitle}
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
      actionButtonProps={actionButtonProps}
    >
      <LoadingModal showing={sendToCustomerLoading} />
      <Wrapper>{renderShareOptions()}</Wrapper>
      <SendEmailModal
        ref={sendEmailModalRef}
        shipmentId={shipmentId || 0}
        usedIn={UsedInEmailTemplates.SHIPMENTS}
        quote={{ reference: containerId || "" } as Sales}
      />
      <ShareShipmentModal ref={shareLinkModalRef} />
    </StylishModal>
  );
};

export default forwardRef(SendCommunicationModal);
